/**
 * vcheckOffline — Offline-Fähigkeit für den V-Check-WP Wizard.
 * ============================================================
 * Iter89z-off (2026-02-26).
 *
 * Dreier-Aufgabe:
 *   1) Formular-Daten (field patches) lokal zwischenspeichern wenn kein
 *      Netz → bei Reconnect in gleicher Reihenfolge nachsenden.
 *   2) Fotos (Blobs) lokal queue'n, wenn der Upload offline fehlschlägt →
 *      bei Reconnect FormData neu bauen + hochladen.
 *   3) Letzten vom Server geladenen Stand pro Projekt cachen, damit das
 *      Formular auch beim Erst-Öffnen offline funktioniert.
 *
 * Konflikt-Strategie (Phase 1 MVP): "letzter Write gewinnt" — kein Merge,
 * kein Conflict-UI. Beim Sync werden alle Patches sequentiell abgesetzt.
 *
 * API:
 *   const off = useVCheckOffline(projectId);
 *   off.enqueuePatch(partialDataObj)   // bei Offline-Fehler
 *   off.enqueuePhoto(sectionKey, File) // bei Offline-Fehler
 *   off.cacheServerSnapshot(doc)       // nach erfolgreichem Load/Patch
 *   off.loadCachedSnapshot()           // beim Erst-Öffnen offline
 *   off.pendingCount // reactive: Patches + Fotos
 *   off.online       // reactive navigator.onLine
 *   off.syncing      // reactive: Sync läuft gerade
 *   off.sync()       // explizit Sync anstoßen
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { openDB } from "idb";
import { toast } from "sonner";
import api from "@/lib/api";


const DB_NAME = "novatus_vcheck_wp";
const DB_VERSION = 1;
const STORE_PATCHES = "patches";      // {id, project_id, patch, ts}
const STORE_PHOTOS = "photos";        // {id, project_id, section_key, blob, name, type, ts}
const STORE_SNAPSHOTS = "snapshots";  // {project_id, doc, ts}


async function _db() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_PATCHES)) {
        const s = db.createObjectStore(STORE_PATCHES, { keyPath: "id", autoIncrement: true });
        s.createIndex("project", "project_id");
      }
      if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
        const s = db.createObjectStore(STORE_PHOTOS, { keyPath: "id", autoIncrement: true });
        s.createIndex("project", "project_id");
      }
      if (!db.objectStoreNames.contains(STORE_SNAPSHOTS)) {
        db.createObjectStore(STORE_SNAPSHOTS, { keyPath: "project_id" });
      }
    },
  });
}


export function useVCheckOffline(projectId, { onSynced } = {}) {
  const [online, setOnline] = useState(() => typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState({ patches: 0, photos: 0 });
  const [syncing, setSyncing] = useState(false);
  const syncLockRef = useRef(false);

  // ------- Zählstand aktualisieren -------
  const refreshCount = useCallback(async () => {
    if (!projectId) return;
    try {
      const db = await _db();
      // Safari committet idle Transaktionen sofort → beide Stores in EINER Transaktion parallel lesen
      const tx = db.transaction([STORE_PATCHES, STORE_PHOTOS], "readonly");
      const [patches, photos] = await Promise.all([
        tx.objectStore(STORE_PATCHES).index("project").getAll(projectId),
        tx.objectStore(STORE_PHOTOS).index("project").getAll(projectId),
      ]);
      setPendingCount({ patches: patches.length, photos: photos.length });
    } catch (e) {
      // IndexedDB nicht verfügbar (iOS Private Mode etc.) — Feature wird einfach deaktiviert
      console.warn("[vcheck-offline] IDB access failed:", e);
    }
  }, [projectId]);

  // ------- Enqueue-Methoden -------
  const enqueuePatch = useCallback(async (patch) => {
    try {
      const db = await _db();
      await db.add(STORE_PATCHES, { project_id: projectId, patch, ts: Date.now() });
      await refreshCount();
    } catch (e) { console.warn("[vcheck-offline] enqueuePatch failed", e); }
  }, [projectId, refreshCount]);

  const enqueuePhoto = useCallback(async (sectionKey, file) => {
    try {
      const db = await _db();
      await db.add(STORE_PHOTOS, {
        project_id: projectId, section_key: sectionKey,
        blob: file, name: file.name, type: file.type || "image/jpeg", ts: Date.now(),
      });
      await refreshCount();
    } catch (e) { console.warn("[vcheck-offline] enqueuePhoto failed", e); }
  }, [projectId, refreshCount]);

  // ------- Snapshot-Cache -------
  const cacheServerSnapshot = useCallback(async (doc) => {
    if (!doc || !projectId) return;
    try {
      const db = await _db();
      await db.put(STORE_SNAPSHOTS, { project_id: projectId, doc, ts: Date.now() });
    } catch (e) { /* ignore */ }
  }, [projectId]);

  const loadCachedSnapshot = useCallback(async () => {
    if (!projectId) return null;
    try {
      const db = await _db();
      const entry = await db.get(STORE_SNAPSHOTS, projectId);
      return entry?.doc || null;
    } catch { return null; }
  }, [projectId]);

  // ------- Sync -------
  const sync = useCallback(async () => {
    if (syncLockRef.current || !projectId) return;
    syncLockRef.current = true;
    setSyncing(true);
    try {
      const db = await _db();
      // 1) Patches sequentiell abschicken (chronologisch per autoIncrement)
      const patches = (await db.getAllFromIndex(STORE_PATCHES, "project", projectId))
        .sort((a, b) => a.id - b.id);
      let patchErrors = 0;
      for (const p of patches) {
        try {
          await api.patch(`/projects/${projectId}/vcheck-wp`, p.patch);
          await db.delete(STORE_PATCHES, p.id);
        } catch (e) {
          patchErrors += 1;
          console.warn("[vcheck-offline] patch sync failed", e);
          // Bei Netzfehler abbrechen — nächster Online-Event versucht erneut.
          if (!navigator.onLine) break;
        }
      }
      // 2) Fotos hochladen
      const photos = (await db.getAllFromIndex(STORE_PHOTOS, "project", projectId))
        .sort((a, b) => a.id - b.id);
      let photoErrors = 0;
      for (const ph of photos) {
        try {
          const form = new FormData();
          const f = new File([ph.blob], ph.name, { type: ph.type });
          form.append("file", f);
          await api.post(
            `/projects/${projectId}/vcheck-wp/photos/${ph.section_key}/upload`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          await db.delete(STORE_PHOTOS, ph.id);
        } catch (e) {
          photoErrors += 1;
          console.warn("[vcheck-offline] photo sync failed", e);
          if (!navigator.onLine) break;
        }
      }
      await refreshCount();
      const synced = patches.length - patchErrors + photos.length - photoErrors;
      if (synced > 0) {
        toast.success(`Offline-Daten synchronisiert: ${synced} Einträge`);
        onSynced?.();
      }
    } finally {
      setSyncing(false);
      syncLockRef.current = false;
    }
  }, [projectId, refreshCount, onSynced]);

  // ------- Online/Offline-Listener -------
  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      // kleiner Debounce, Netz braucht einen Tick bis es wirklich tut
      setTimeout(() => sync(), 500);
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [sync]);

  // Initial-Zählstand
  useEffect(() => { refreshCount(); }, [refreshCount]);

  return {
    online, syncing, pendingCount,
    enqueuePatch, enqueuePhoto,
    cacheServerSnapshot, loadCachedSnapshot,
    sync, refreshCount,
  };
}


// --- Helfer: ist der Fehler ein Netzwerk-/Offline-Fehler? ---
export function isOfflineError(err) {
  if (!err) return false;
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  const msg = (err.message || "").toLowerCase();
  if (!err.response && (msg.includes("network") || msg.includes("fetch")
     || msg.includes("timeout") || msg.includes("err_internet_disconnected"))) {
    return true;
  }
  // axios: `err.code === 'ERR_NETWORK'` wenn Server unerreichbar
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  return false;
}
